import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
} from "lucide-react";

import { AdminEventAPI } from "../../services/adminEvent.api";
import Toast from "../../components/Toast";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  async function fetchEvent() {
    try {
      setLoading(true);
      const res = await AdminEventAPI.get(id);
      setEvent(res.data);
    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to load event",
      });
    } finally {
      setLoading(false);
    }
  }

  async function toggleStatus() {
    try {
      setActionLoading(true);
      const newStatus =
        event.status === "published" ? "draft" : "published";

      const res = await AdminEventAPI.update(id, {
        status: newStatus,
      });

      setEvent(res.data);

      setToast({
        type: "success",
        message:
          newStatus === "published"
            ? "Event published"
            : "Event set to draft",
      });
    } catch {
      setToast({
        type: "error",
        message: "Failed to update status",
      });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDelete() {
    const ok = window.confirm(
      "Delete this event? This action cannot be undone."
    );
    if (!ok) return;

    try {
      setActionLoading(true);
      await AdminEventAPI.remove(id);
      navigate("/admin/dashboard");
    } catch {
      setToast({
        type: "error",
        message: "Failed to delete event",
      });
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) {
    return <div className="event-detail loading">Loading event…</div>;
  }

  if (!event) {
    return <div className="event-detail">Event not found</div>;
  }

  return (
    <div className="event-detail">
      {/* ===== TOP BAR ===== */}
      <div className="detail-top">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>

        <div className="actions">
          <button
            className="btn btn-secondary"
            onClick={toggleStatus}
            disabled={actionLoading}
          >
            {event.status === "published" ? (
              <>
                <EyeOff size={16} /> Unpublish
              </>
            ) : (
              <>
                <Eye size={16} /> Publish
              </>
            )}
          </button>

          <button
            className="btn btn-outline"
            onClick={() =>
              navigate(`/admin/events/${id}/edit`)
            }
          >
            <Pencil size={16} /> Edit
          </button>

          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={actionLoading}
          >
            <Trash2 size={16} /> Delete
          </button>
        </div>
      </div>

      {/* ===== HERO ===== */}
      <div className="event-hero">
        {event.headerImage?.url && (
          <img
            src={event.headerImage.url}
            alt={event.title}
          />
        )}

        <div className="hero-content">
          <span className={`status-pill ${event.status}`}>
            {event.status}
          </span>
          <h1>{event.title}</h1>
        </div>
      </div>

      {/* ===== INFO ===== */}
      <div className="event-info-grid">
        <Info label="Date">
          {new Date(event.date).toLocaleDateString(
            "id-ID",
            { dateStyle: "full" }
          )}
        </Info>

        <Info label="Time">
          {event.timeStart || "-"} –{" "}
          {event.timeEnd || "-"}
        </Info>

        <Info label="Location">
          {event.location?.name || "-"}
          <br />
          <small>{event.location?.address}</small>
        </Info>

        <Info label="Ticket Price">
          {event.price === 0
            ? "Free"
            : `Rp ${event.price.toLocaleString(
                "id-ID"
              )}`}
        </Info>

        <Info label="Max Tickets">
          {event.maxTickets || "Unlimited"}
        </Info>

        <Info label="Created At">
          {new Date(
            event.createdAt
          ).toLocaleString("id-ID")}
        </Info>
      </div>

      {/* ===== TICKET TIERS ===== */}
      {event.ticketTiers?.length > 0 && (
        <div className="tier-breakdown">
          <h3>Ticket Tiers</h3>
          <table className="tier-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Price</th>
                <th>Quota</th>
                <th>Sold</th>
                <th>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {event.ticketTiers.map((tier) => (
                <tr key={tier._id}>
                  <td>{tier.name}</td>
                  <td>Rp {tier.price?.toLocaleString("id-ID")}</td>
                  <td>{tier.quota}</td>
                  <td>{tier.sold ?? 0}</td>
                  <td>{tier.remaining ?? tier.quota}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <Toast {...toast} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div className="info-card">
      <span className="info-label">{label}</span>
      <div className="info-value">{children}</div>
    </div>
  );
}
